import { useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { v4 as uuidv4 } from 'uuid';
import { resolveClientLocale } from 'lib/i18n/browser';
import { useLocale } from 'lib/i18n/client';
import { getTranslations } from 'lib/i18n/dictionary';

import {
  fetchLanguages,
  type ProfileLanguage,
  searchLanguages,
} from './profile.api';

interface LanguagesAutocompleteProps {
  accessToken: string | null;
  value: ProfileLanguage[];
  onChange: (languages: ProfileLanguage[]) => void;
  disabled?: boolean;
}

function normalizeBrowserLocale() {
  return resolveClientLocale();
}

function normalizeLanguageTitle(value: string) {
  return value.trim().replace(/\s+/g, ' ').slice(0, 128);
}

function normalizeLanguageKey(value: string) {
  const normalizedValue = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalizedValue.slice(0, 64);
}

function getLanguageIdentity(language: ProfileLanguage) {
  const normalizedKey = normalizeLanguageKey(language.key);
  if (normalizedKey.length) {
    return `key:${normalizedKey}`;
  }

  const normalizedTitle = normalizeLanguageTitle(language.title).toLowerCase();
  if (normalizedTitle.length) {
    return `title:${normalizedTitle}`;
  }

  const normalizedUuid = language.uuid.trim();
  return `uuid:${normalizedUuid}`;
}

function normalizeLanguage(language: ProfileLanguage): ProfileLanguage | null {
  const normalizedTitle = normalizeLanguageTitle(language.title);
  const trimmedKey = language.key.trim().slice(0, 64);
  const normalizedUuid = language.uuid.trim() || uuidv4();
  const normalizedTitleKey = normalizeLanguageKey(normalizedTitle);
  const normalizedKey =
    trimmedKey ||
    normalizedTitleKey ||
    `language-${normalizedUuid.replace(/-/g, '').slice(0, 12)}`;

  if (!normalizedTitle) {
    return null;
  }

  return {
    uuid: normalizedUuid,
    key: normalizedKey,
    title: normalizedTitle,
  };
}

function resolveTypedLanguage(
  rawValue: string,
  languagesPool: ProfileLanguage[]
): ProfileLanguage | null {
  const normalizedTitle = normalizeLanguageTitle(rawValue);
  if (!normalizedTitle.length) {
    return null;
  }

  const normalizedKey = normalizeLanguageKey(normalizedTitle);
  const normalizedTitleLowerCase = normalizedTitle.toLowerCase();
  const matchingLanguage = languagesPool.find((language) => {
    const normalizedLanguageTitle = normalizeLanguageTitle(language.title).toLowerCase();
    const normalizedLanguageKey = normalizeLanguageKey(language.key);
    return (
      normalizedLanguageTitle === normalizedTitleLowerCase ||
      (normalizedKey.length > 0 && normalizedLanguageKey === normalizedKey)
    );
  });

  if (matchingLanguage) {
    return normalizeLanguage(matchingLanguage);
  }

  const generatedUuid = uuidv4();
  return {
    uuid: generatedUuid,
    key: normalizedKey || `language-${generatedUuid.replace(/-/g, '').slice(0, 12)}`,
    title: normalizedTitle,
  };
}

function normalizeSelectedLanguages(
  selectedLanguages: Array<ProfileLanguage | string>,
  languagesPool: ProfileLanguage[]
) {
  const uniqueLanguages = new Map<string, ProfileLanguage>();

  for (const selectedLanguage of selectedLanguages) {
    const normalizedLanguage =
      typeof selectedLanguage === 'string'
        ? resolveTypedLanguage(selectedLanguage, languagesPool)
        : normalizeLanguage(selectedLanguage);
    if (!normalizedLanguage) {
      continue;
    }

    const languageIdentity = getLanguageIdentity(normalizedLanguage);
    if (uniqueLanguages.has(languageIdentity)) {
      continue;
    }

    uniqueLanguages.set(languageIdentity, normalizedLanguage);
  }

  return Array.from(uniqueLanguages.values());
}

function mergeLanguageOptions(primary: ProfileLanguage[], secondary: ProfileLanguage[]) {
  const byIdentity = new Map<string, ProfileLanguage>();

  for (const language of primary) {
    const normalizedLanguage = normalizeLanguage(language);
    if (!normalizedLanguage) {
      continue;
    }
    byIdentity.set(getLanguageIdentity(normalizedLanguage), normalizedLanguage);
  }

  for (const language of secondary) {
    const normalizedLanguage = normalizeLanguage(language);
    if (!normalizedLanguage) {
      continue;
    }
    byIdentity.set(getLanguageIdentity(normalizedLanguage), normalizedLanguage);
  }

  return Array.from(byIdentity.values());
}

export function LanguagesAutocomplete({
  accessToken,
  value,
  onChange,
  disabled = false,
}: LanguagesAutocompleteProps) {
  const locale = useLocale();
  const t = getTranslations(locale).adminInputs;
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ProfileLanguage[]>([]);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const browserLocale = useMemo(() => normalizeBrowserLocale(), []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    fetchLanguages(browserLocale, 50, 0, accessToken ?? undefined)
      .then((languages) => {
        if (cancelled) {
          return;
        }

        setOptions((currentOptions) => mergeLanguageOptions(currentOptions, languages));
      })
      .catch(() => {
        if (!cancelled) {
          setOptions((currentOptions) => currentOptions);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, browserLocale]);

  useEffect(() => {
    const normalizedQuery = query.trim();
    if (!normalizedQuery.length) {
      setIsOptionsOpen(false);
      return;
    }

    let cancelled = false;
    const timerId = window.setTimeout(() => {
      setIsLoading(true);

      searchLanguages(normalizedQuery, browserLocale, accessToken ?? undefined)
        .then((languages) => {
          if (cancelled) {
            return;
          }

          const normalizedLanguages = mergeLanguageOptions(languages, value);
          setOptions((currentOptions) =>
            mergeLanguageOptions(currentOptions, normalizedLanguages)
          );
          setIsOptionsOpen(normalizedLanguages.length > 0);
        })
        .catch(() => {
          if (!cancelled) {
            setIsOptionsOpen(false);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoading(false);
          }
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [query, value, accessToken, browserLocale]);

  const combinedOptions = useMemo(() => mergeLanguageOptions(options, value), [options, value]);
  const combinedOptionsByIdentity = useMemo(() => {
    const optionsByIdentity = new Map<string, ProfileLanguage>();

    for (const option of combinedOptions) {
      const languageIdentity = getLanguageIdentity(option);
      if (optionsByIdentity.has(languageIdentity)) {
        continue;
      }

      optionsByIdentity.set(languageIdentity, option);
    }

    return optionsByIdentity;
  }, [combinedOptions]);

  useEffect(() => {
    if (!value.length || !combinedOptionsByIdentity.size) {
      return;
    }

    const localizedLanguages = value.map((language) => {
      const normalizedLanguage = normalizeLanguage(language);
      if (!normalizedLanguage) {
        return language;
      }

      return (
        combinedOptionsByIdentity.get(getLanguageIdentity(normalizedLanguage)) ??
        normalizedLanguage
      );
    });

    const hasChanges = localizedLanguages.some((language, index) => {
      const currentLanguage = value[index];
      return (
        language.uuid !== currentLanguage.uuid ||
        language.key !== currentLanguage.key ||
        language.title !== currentLanguage.title
      );
    });

    if (hasChanges) {
      onChange(localizedLanguages);
    }
  }, [combinedOptionsByIdentity, onChange, value]);

  return (
    <Autocomplete<ProfileLanguage, true, false, true>
      multiple
      freeSolo
      forcePopupIcon
      options={combinedOptions}
      value={value}
      disabled={disabled}
      loading={isLoading}
      open={isOptionsOpen && combinedOptions.length > 0}
      onOpen={() => {
        if (combinedOptions.length > 0) {
          setIsOptionsOpen(true);
        }
      }}
      onClose={() => {
        setIsOptionsOpen(false);
      }}
      filterOptions={(filteredOptions) => filteredOptions}
      isOptionEqualToValue={(option, selectedLanguage) =>
        typeof selectedLanguage !== 'string' && option.uuid === selectedLanguage.uuid
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.title)}
      onInputChange={(_, nextQuery, reason) => {
        if (reason === 'input') {
          setQuery(nextQuery);
          if (!nextQuery.trim().length) {
            setIsOptionsOpen(false);
          }
        } else if (reason === 'clear' || reason === 'reset') {
          setQuery('');
          setIsOptionsOpen(false);
        }
      }}
      onChange={(_, nextLanguages) => {
        const normalizedLanguages = normalizeSelectedLanguages(
          nextLanguages,
          mergeLanguageOptions(options, value)
        );
        onChange(normalizedLanguages);
        setOptions((currentOptions) =>
          mergeLanguageOptions(currentOptions, normalizedLanguages)
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t.searchLanguagesPlaceholder}
          helperText={t.searchLanguagesHelper}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
